(() => {
  const TOKENS = {
    duration: 920,
    stagger: 60,
    threshold: 0.18,
    rootMargin: '0px 0px -8% 0px'
  };

  const SECTION_SELECTOR = [
    '.evolvant-home__hero',
    '.evolvant-home__story-rail',
    '.evolvant-home__split',
    '.evolvant-home__differentiator',
    '.evolvant-home__platforms',
    '.evolvant-home__services',
    '.evolvant-home__about',
    '.evolvant-home__cta',
    '.creators-page-hero',
    '.creators-page-grid',
    '.creators-page-cta',
    '.brands-page-hero',
    '.brands-page-grid',
    '.brands-page-outcomes',
    '.brands-page-cta',
    '.agencies-page-hero',
    '.agencies-page-impact',
    '.agencies-page-work',
    '.agencies-page-preview',
    '.agency-page-hero',
    '.agency-page-grid',
    '.agency-page-philosophy',
    '.agency-page-split',
    '.agency-page-team',
    '.agency-page-cta',
    '.agency-page-contact-form',
    '.differentiator-page-hero',
    '.differentiator-page-why',
    '.differentiator-page-pillars',
    '.differentiator-page-system',
    '.differentiator-page-team',
    '.differentiator-page-philosophy',
    '.differentiator-page-cta'
  ].join(', ');

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let observer = null;

  const unique = (nodes) => Array.from(new Set(nodes.filter(Boolean)));
  const afterNextPaint = (callback) => {
    if (typeof callback !== 'function') return;

    if (typeof window.requestAnimationFrame !== 'function') {
      window.setTimeout(callback, 34);
      return;
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(callback);
    });
  };

  const pick = (scope, selectors) => {
    if (!scope) return null;
    for (const selector of selectors) {
      const node = scope.querySelector(selector);
      if (node) return node;
    }
    return null;
  };

  const pickAll = (scope, selectors) => {
    if (!scope) return [];
    const nodes = [];
    selectors.forEach((selector) => {
      scope.querySelectorAll(selector).forEach((node) => nodes.push(node));
    });
    return unique(nodes);
  };

  const getBaseY = () => 22;
  const getStagger = () => TOKENS.stagger;
  const getRevealTargets = (scope = document) => {
    const targets = [];

    if (scope instanceof Element && scope.matches('[data-evolvant-reveal]')) {
      targets.push(scope);
    }

    if (typeof scope.querySelectorAll === 'function') {
      scope.querySelectorAll('[data-evolvant-reveal]').forEach((target) => targets.push(target));
    }

    return unique(targets);
  };

  const markReveal = (node, options = {}) => {
    if (!node) return;

    node.dataset.evolvantReveal = '';

    const delay = Math.max(0, Math.round(options.delay ?? 0));
    node.style.setProperty('--evolvant-reveal-delay', `${delay}ms`);
  };

  const markStagger = (nodes, startDelay, options = {}) => {
    const step = options.step ?? getStagger();
    nodes.forEach((node, index) => {
      markReveal(node, {
        ...options,
        delay: startDelay + step * index
      });
    });
  };

  const revealIntro = (section, options = {}) => {
    const intro = pick(section, options.introSelectors || ['[class*="__intro"]', '[class*="__content"]', '[class*="__copy"]']);
    if (!intro) return options.startDelay ?? 0;

    const baseDelay = options.startDelay ?? 0;
    const eyebrow = pick(intro, options.eyebrowSelectors || ['[class*="__eyebrow"]']);
    const heading = pick(intro, options.headingSelectors || ['h1', 'h2', '[class*="__heading"]']);
    const body = pick(intro, options.bodySelectors || ['[class*="__body"]', 'p:not([class*="__eyebrow"])']);
    const actions = pick(intro, options.actionsSelectors || ['[class*="__actions"]']);

    markReveal(eyebrow, { delay: baseDelay, y: getBaseY() - 4 });
    markReveal(heading, { delay: baseDelay + 60, y: getBaseY() });
    markReveal(body, { delay: baseDelay + 120, y: getBaseY() - 5, duration: 590 });
    markReveal(actions, { delay: baseDelay + 180, y: getBaseY() - 5, duration: 580 });

    return baseDelay + 180;
  };

  const configureHeroSection = (section) => {
    if (section.matches('.evolvant-home__hero')) {
      markReveal(pick(section, ['.evolvant-home__hero-lockup']), {
        delay: 0
      });
      markReveal(pick(section, ['.evolvant-home__hero-wave']), {
        delay: 60
      });
      return;
    }

    const introEnd = revealIntro(section, {
      introSelectors: ['[class*="__content"]', '[class*="__copy"]']
    });

    const visual = pick(section, ['[class*="__visual"]', '[class*="__canvas"]', '[class*="__system"]']);
    markReveal(visual, {
      delay: Math.max(120, introEnd),
      y: getBaseY() - 8,
      scale: 0.992,
      duration: 640
    });

    if (section.matches('.creators-page-hero')) {
      markReveal(pick(section, ['.creators-page-hero__system-top']), {
        delay: Math.max(120, introEnd + 40)
      });

      markStagger(
        pickAll(section, ['.creators-page-hero__system-panel']),
        Math.max(180, introEnd + 100),
        {
          duration: 590,
          y: getBaseY() - 6,
          scale: 0.998
        }
      );
    }

    if (section.matches('.brands-page-hero')) {
      markReveal(pick(section, ['.brands-page-hero__system-top']), {
        delay: Math.max(120, introEnd + 40)
      });

      markStagger(
        pickAll(section, ['.brands-page-hero__system-panel']),
        Math.max(180, introEnd + 100),
        {
          duration: 590,
          y: getBaseY() - 6,
          scale: 0.998
        }
      );
    }

    if (section.matches('.agencies-page-hero')) {
      markReveal(pick(section, ['.agencies-page-hero__system-top']), {
        delay: Math.max(120, introEnd + 40)
      });

      markStagger(
        pickAll(section, ['.agencies-page-hero__system-panel']),
        Math.max(180, introEnd + 100),
        {
          duration: 590,
          y: getBaseY() - 6,
          scale: 0.998
        }
      );
    }
  };

  const configureGridSection = (section) => {
    if (section.matches('.evolvant-home__story-rail')) {
      markReveal(pick(section, ['.evolvant-home__rail-section-label']), {
        delay: 0
      });
      markReveal(pick(section, ['.evolvant-home__rail-stack']), {
        delay: 60
      });
      return;
    }

    if (section.matches('.evolvant-home__split')) {
      markReveal(pick(section, ['.evolvant-home__section-intro']), {
        delay: 0
      });
      markStagger(
        pickAll(section, ['.evolvant-home__preview-block']),
        60,
        {
          duration: 590,
          y: getBaseY() - 6,
          scale: 0.998
        }
      );
      return;
    }

    if (section.matches('.evolvant-home__differentiator')) {
      markReveal(pick(section, ['.evolvant-home__section-intro--differentiator']), {
        delay: 0
      });
      markReveal(pick(section, ['.evolvant-home__differentiator-main-stage']), {
        delay: 60
      });
      markReveal(pick(section, ['.evolvant-home__differentiator-support-stage']), {
        delay: 120
      });
      return;
    }

    if (section.matches('.evolvant-home__platforms')) {
      markReveal(pick(section, ['.evolvant-home__platforms-heading']), {
        delay: 0
      });
      markStagger(
        pickAll(section, ['.evolvant-home__platform-chip']),
        60,
        {
          duration: 590,
          y: getBaseY() - 6,
          scale: 0.998
        }
      );
      return;
    }

    if (section.matches('.evolvant-home__services')) {
      markReveal(pick(section, ['.evolvant-home__section-intro--wide']), {
        delay: 0
      });
      markStagger(
        pickAll(section, ['.evolvant-home__service-card']),
        60,
        {
          duration: 590,
          y: getBaseY() - 6,
          scale: 0.998
        }
      );
      markReveal(pick(section, ['.evolvant-home__services-footer']), {
        delay: 120
      });
      return;
    }

    if (section.matches('.evolvant-home__about')) {
      markReveal(pick(section, ['.evolvant-home__about-copy']), {
        delay: 0
      });
      markReveal(pick(section, ['.evolvant-home__about-card--team']), {
        delay: 60
      });
      return;
    }

    const introEnd = revealIntro(section, {
      introSelectors: ['[class*="__intro"]', '[class*="__copy"]', '[class*="__content"]']
    });

    if (section.matches('.creators-page-grid--problem')) {
      markReveal(pick(section, ['.creators-page-grid__inner']), {
        delay: 60,
        y: getBaseY() - 6,
        duration: 600
      });
    }

    if (section.matches('.brands-page-grid--problem')) {
      markReveal(pick(section, ['.brands-page-grid__inner']), {
        delay: 60,
        y: getBaseY() - 6,
        duration: 600
      });
    }

    const visual = pick(section, [
      '.creators-page-grid__system-visual',
      '.brands-page-grid__system-visual',
      '.agencies-page-impact__frame',
      '.agencies-page-preview__visual',
      '.agencies-page-preview__stage',
      '.agency-page-grid__pillars-layout',
      '.agency-page-team__grid',
      '.brands-page-outcomes__field',
      '.agencies-page-work__shell',
      '.differentiator-page-pillars__surface',
      '.differentiator-page-system__map',
      '.differentiator-page-philosophy__stage'
    ]);
    markReveal(visual, {
      delay: introEnd + 60,
      y: getBaseY() - 7,
      scale: 0.994,
      duration: 620
    });

    if (section.matches('.creators-page-grid--system')) {
      markReveal(pick(section, ['.creators-page-grid__system-visual-shell']), {
        delay: introEnd + 90
      });

      markStagger(
        pickAll(section, ['.creators-page-grid__system-screen']),
        introEnd + 140,
        {
          duration: 580,
          y: getBaseY() - 5,
          scale: 0.998
        }
      );
    }

    if (section.matches('.differentiator-page-why')) {
      markStagger(
        pickAll(section, ['.differentiator-page-why__card']),
        introEnd + 60,
        {
          duration: 590,
          y: getBaseY() - 6,
          scale: 0.998
        }
      );
      return;
    }

    const cards = pickAll(section, [
      '.creators-page-grid__card',
      '.brands-page-grid__card',
      '.agency-page-grid__card',
      '.agency-page-grid__pillar-item',
      '.agency-page-team__card',
      '.brands-page-outcomes__card',
      '.agencies-page-work__step',
      '.differentiator-page-why__card',
      '.differentiator-page-pillars__card',
      '.differentiator-page-system__card',
      '.differentiator-page-philosophy__card'
    ]);

    markStagger(cards, introEnd + 60, {
      duration: 590,
      y: getBaseY() - 6,
      scale: 0.998
    });
  };

  const configureCtaSection = (section) => {
    if (section.matches('.evolvant-home__cta')) {
      markReveal(pick(section, ['.evolvant-home__cta-shell']), {
        delay: 0
      });
      return;
    }

    const shell = pick(section, ['[class*="__shell"]', '[class*="__surface"]']);
    const content = pick(section, ['[class*="__content"]', '[class*="__copy"]']) || section;

    markReveal(shell, {
      delay: 0,
      y: getBaseY() - 6,
      scale: 0.996,
      duration: 630
    });

    const eyebrow = pick(content, ['[class*="__eyebrow"]']);
    const heading = pick(content, ['h2', '[class*="__heading"]']);
    const body = pick(content, ['[class*="__body"]']);
    const actions = pick(content, ['[class*="__actions"]', '[class*="__action"]']);
    const rail = pick(section, ['.creators-page-cta__rail']);

    markReveal(eyebrow, { delay: 60, y: getBaseY() - 5, duration: 570 });
    markReveal(heading, { delay: 120, y: getBaseY() - 3, duration: 620 });
    markReveal(body, { delay: 180, y: getBaseY() - 5, duration: 580 });
    markReveal(actions, { delay: 240, y: getBaseY() - 6, duration: 560 });
    markReveal(rail, { delay: 90, y: getBaseY() - 6, duration: 600 });
  };

  const configureSplitSection = (section) => {
    const introEnd = revealIntro(section, {
      introSelectors: ['.agency-page-split__copy'],
      actionsSelectors: ['.agency-page-split__actions', '.agency-page-split__services-action']
    });

    const points = pickAll(section, ['.agency-page-split__point']);
    markStagger(points, introEnd + 60, {
      duration: 590,
      y: getBaseY() - 6,
      scale: 0.998
    });

    const visual = pick(section, ['.agency-page-split__visual', '.agency-page-split__frame']);
    markReveal(visual, {
      delay: introEnd + 60,
      y: getBaseY() - 8,
      scale: 0.994,
      duration: 620
    });
  };

  const configurePhilosophySection = (section) => {
    const introEnd = revealIntro(section, {
      introSelectors: ['.agency-page-philosophy__intro']
    });

    if (section.matches('.agency-page-philosophy--evolvant')) return;

    const media = pick(section, ['.agency-page-philosophy__media', '.agency-page-philosophy__grid']);
    markReveal(media, {
      delay: introEnd + 60,
      y: getBaseY() - 7,
      scale: 0.994,
      duration: 620
    });

    const cards = pickAll(section, [
      '.agency-page-philosophy__card'
    ]);
    markStagger(cards, introEnd + 60, {
      duration: 590,
      y: getBaseY() - 6,
      scale: 0.998
    });
  };

  const configureContactSection = (section) => {
    const introEnd = revealIntro(section, {
      introSelectors: ['.agency-page-contact-form__intro']
    });

    markReveal(pick(section, ['.agency-page-contact-form__surface', '.agency-page-contact-form__form']), {
      delay: introEnd + 60,
      y: getBaseY() - 6,
      scale: 0.995,
      duration: 620
    });
  };

  const configureSection = (section) => {
    if (section.matches('.about-page-rebuild, .evolvant-home')) return;

    if (section.matches('.evolvant-home__hero, .creators-page-hero, .brands-page-hero, .agencies-page-hero, .agency-page-hero, .differentiator-page-hero')) {
      configureHeroSection(section);
      return;
    }

    if (section.matches('.evolvant-home__story-rail, .evolvant-home__split, .evolvant-home__differentiator, .evolvant-home__platforms, .evolvant-home__services, .evolvant-home__about, .creators-page-grid, .brands-page-grid, .agency-page-grid, .brands-page-outcomes, .agencies-page-impact, .agencies-page-work, .agencies-page-preview, .agency-page-team, .differentiator-page-why, .differentiator-page-pillars, .differentiator-page-system, .differentiator-page-team, .differentiator-page-philosophy')) {
      configureGridSection(section);
      return;
    }

    if (section.matches('.evolvant-home__cta, .creators-page-cta, .brands-page-cta, .agency-page-cta, .differentiator-page-cta')) {
      configureCtaSection(section);
      return;
    }

    if (section.matches('.agency-page-split')) {
      configureSplitSection(section);
      return;
    }

    if (section.matches('.agency-page-philosophy')) {
      configurePhilosophySection(section);
      return;
    }

    if (section.matches('.agency-page-contact-form')) {
      configureContactSection(section);
      return;
    }

    markReveal(section, { delay: 0, y: getBaseY() - 4, duration: TOKENS.duration });
  };

  const getSectionsFromScope = (scope) => {
    const sections = [];
    if (!scope) return sections;
    if (scope instanceof Element && scope.matches(SECTION_SELECTOR)) {
      sections.push(scope);
    }
    if (typeof scope.querySelectorAll === 'function') {
      scope.querySelectorAll(SECTION_SELECTOR).forEach((section) => sections.push(section));
    }
    return unique(sections);
  };

  const getObserver = () => {
    if (observer) return observer;

    observer = new IntersectionObserver(
      (entries, io) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        });
      },
      {
        threshold: TOKENS.threshold,
        rootMargin: TOKENS.rootMargin
      }
    );

    return observer;
  };

  const observeReveals = (scope = document) => {
    const targets = getRevealTargets(scope);
    if (!targets.length) return;

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach((target) => target.classList.add('is-visible'));
      return;
    }

    const io = getObserver();

    targets.forEach((target) => {
      if (target.classList.contains('is-visible')) return;
      io.observe(target);
    });
  };

  const INITIAL_LOAD_REVEAL_DELAY = 40;

const applyMotionSystem = (scope = document, options = {}) => {
  getSectionsFromScope(scope).forEach((section) => configureSection(section));

  if (options.deferInitialReveal) {
    afterNextPaint(() => {
      window.setTimeout(() => {
        observeReveals(scope);
      }, INITIAL_LOAD_REVEAL_DELAY);
    });
    return;
  }

  observeReveals(scope);
};

  const init = () => {
    document.documentElement.classList.add('evolvant-motion-ready');
    if (!document.body) return;
    document.body.classList.add('evolvant-motion-ready');
    applyMotionSystem(document, { deferInitialReveal: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (!event.target) return;
    applyMotionSystem(event.target);
  });

  const handleMotionPreferenceChange = () => {
    if (reducedMotion.matches && observer) {
      observer.disconnect();
      observer = null;
    }
    applyMotionSystem(document);
  };

  if (typeof reducedMotion.addEventListener === 'function') {
    reducedMotion.addEventListener('change', handleMotionPreferenceChange);
  } else if (typeof reducedMotion.addListener === 'function') {
    reducedMotion.addListener(handleMotionPreferenceChange);
  }

})();
